import React, { useEffect, useRef, useState } from 'react';

import { FieldArray, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import MilestoneRow from './MilestoneRow';

import Button from 'CommonComponents/Button';
import { DragAndDrop, Drag, Drop } from 'CommonComponents/DragAndDrop';
import Basic from 'CommonComponents/slider/Basic';
import TextButton from 'CommonComponents/TextButton';

import { useToast } from 'Hooks/useToast';

import TrashIcon from 'Assets/icons/trash.svg';

const DEFAULT_MILESTONE = { id: null, name: '', date: '', description: null, perc: 0, type: 'fixed/cash' };
const MilestoneSectionSchema = Yup.object().shape({
    dates: Yup.array().of(
        Yup.object().shape({
            date: Yup.date().required($t('This value is required')),
            name: Yup.string().nullable(),
            description: Yup.string().nullable(),
            perc: Yup.number()
                .min(0, $t('Percentage must be at least 0'))
                .max(100, $t('Percentage must be 100 or less'))
                .required($t('This value is required')),
        }),
    ),
});

export default function MilestoneSectionForm({ showCancelButton = true, ...props }) {
    const [totalPercentage, setTotalPercentage] = useState(100);
    const [initialMilestones, setInitialMilestones] = useState([]);
    const [isConfirmed, setIsConfirmed] = useState(props.milestonesConfirmed);

    const toast = useToast();

    useEffect(() => {
        if (!props.milestones || props.milestones.length === 0) {
            setInitialMilestones([]);
            return;
        }

        // Pass 1: compute all fields that other milestones depend on (perc)
        const firstPass = props.milestones.map((milestone) => ({
            ...milestone,
            description: milestone.description ?? DEFAULT_MILESTONE.description,
            name: milestone.name ?? DEFAULT_MILESTONE.name,
            date: milestone.date ?? DEFAULT_MILESTONE.date,
            perc: Math.round(milestone.percentage) ?? DEFAULT_MILESTONE.percentage,
            type: milestone.type ?? DEFAULT_MILESTONE.type,
            canDeleteMilestones: props.canDeleteMilestones,
        }));

        // Pass 2: compute derived fields using the correct perc values so FormObserver
        // finds nothing to update on mount and triggers no initial cascade.
        const withDates = { dates: firstPass };
        setInitialMilestones(
            firstPass.map((milestone, index) => ({
                ...milestone,
                uniqueKey: getUniqueKeyBasedOnIndex(withDates, index),
                maxPercentage: getMaxPercentage(withDates, index) ?? DEFAULT_MILESTONE.percentage,
            })),
        );
    }, [props.milestones]);

    const getMaxPercentage = (values, selectedIndex) => {
        let percentage = 0;
        values.dates?.map((milestone, index) => {
            if (index != selectedIndex) {
                percentage += milestone.perc;
            }
        });

        return 100 - percentage;
    };

    const getDefaultMilestone = (values) => {
        let milestone = DEFAULT_MILESTONE;
        milestone.maxPercentage = 100 - totalPercentage;
        milestone.canDeleteMilestones = true;
        milestone.uniqueKey = getUniqueKeyBasedOnIndex(values, (values?.dates?.length || 0) + 1);

        return milestone;
    };

    const valuesListener = (percentage) => {
        percentage = parseInt(percentage);
        setTotalPercentage(percentage);
        if (props.setMilestonesConfirmed) {
            props.setMilestonesConfirmed(false);
            setIsConfirmed(false);
        }
    };

    const redirectBack = () => {
        window.location.href = props.redirectRoute;
    };

    const showActionButtons = () => {
        return props.canEdit && (props.parentSection === 'expense' || (props.parentSection !== 'expense' && !isConfirmed));
    };

    const getUniqueKeyBasedOnIndex = (values, index) => {
        if ((values?.dates?.length || 0) === 0) {
            return 0;
        }
        return values?.dates?.length + index;
    };

    return (
        initialMilestones &&
        initialMilestones.length > 0 && (
            <div>
                <Formik
                    enableReinitialize
                    initialValues={{
                        dates: initialMilestones,
                        section: `fixed/${props.section}`,
                    }}
                    validationSchema={MilestoneSectionSchema}
                    onSubmit={(values, actions) => {
                        let data = { ...values };

                        axios
                            .post(props.postRoute, data)
                            .catch((e) => {
                                console.log(e);
                                actions.setSubmitting(false);
                                actions.setErrors(e.response.data.errors);
                            })
                            .finally(() => {
                                actions.setSubmitting(false);
                                toast.success(props.parentSection === 'expense' ? $t(`${props.resource.name} cashflow updated`) : $t('Milestones confirmed'));
                                if (props.redirectRoute != '' && props.parentSection === 'expense') {
                                    redirectBack();
                                }
                                if (props.setMilestonesConfirmed) {
                                    props.setMilestonesConfirmed(true);
                                    setIsConfirmed(true);
                                }
                            });
                    }}
                >
                    {({ values, errors, touched, isSubmitting, handleSubmit, setFieldValue }) => (
                        <div className="tw-mt-4">
                            <FormObserver
                                valuesListener={valuesListener}
                                getMaxPercentage={getMaxPercentage}
                                getUniqueKeyBasedOnIndex={getUniqueKeyBasedOnIndex}
                            />
                            <div className="tw-flex tw-items-center tw-gap-x-4 tw-w-full tw-p-4 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow dark:tw-bg-gray-800 dark:tw-border-gray-700">
                                <h5 className="tw-font-semibold tw-mb-0.5">{props.sectionTitle}</h5>
                                <Basic values={[totalPercentage]} disabled></Basic>
                            </div>

                            <div className="tw-flex tw-flex-col tw-gap-y-2">
                                <h5 className="tw-text-blue-500 tw-font-bold tw-mt-4">{$t('Milestones')}</h5>
                                <FieldArray
                                    enableReinitialize
                                    name="dates"
                                    render={(arrayHelpers) => (
                                        <div className="tw-grid tw-grid-cols-1 tw-p-4 !tw-bg-[#f1f1f1] tw-rounded-lg tw-shadow">
                                            <DragAndDrop
                                                onDragEnd={(result) => {
                                                    const { type, source, destination } = result;

                                                    if (!destination) return;

                                                    const sourceIndex = source.index;
                                                    const destinationIndex = destination.index;

                                                    // Reordering milestones
                                                    if (type === 'droppable-milestone') {
                                                        arrayHelpers.move(sourceIndex, destinationIndex);
                                                    }
                                                }}
                                            >
                                                <Drop id="droppable" type="droppable-milestone" draggingOverClassNames="tw-bg-blue-25">
                                                    {values.dates?.map((milestone, index) => {
                                                        const milestoneErrors = errors.dates && errors.dates[index];
                                                        const milestoneTouched = touched.dates && touched.dates[index];

                                                        return (
                                                            <Drag
                                                                dragHandlerColor={'blue'}
                                                                key={`milestone-${index}`}
                                                                id={`milestone-${index}`}
                                                                index={index}
                                                                dragHandlerClasses="tw-pt-2"
                                                                className="tw-bg-none tw-mb-3"
                                                            >
                                                                <MilestoneRow
                                                                    key={index}
                                                                    index={index}
                                                                    section={props.section}
                                                                    minDate={props.minDate}
                                                                    maxDate={props.maxDate}
                                                                    values={values}
                                                                    errors={milestoneErrors}
                                                                    touched={milestoneTouched}
                                                                    setFieldValue={setFieldValue}
                                                                    actions={
                                                                        (values?.dates?.length || 0) > 1 &&
                                                                        milestone.canDeleteMilestones && (
                                                                            <TextButton variant="danger" onClick={() => arrayHelpers.remove(index)}>
                                                                                <TrashIcon className="tw-w-3 tw-h-3" />
                                                                            </TextButton>
                                                                        )
                                                                    }
                                                                ></MilestoneRow>
                                                            </Drag>
                                                        );
                                                    })}
                                                </Drop>
                                            </DragAndDrop>
                                            {totalPercentage < 100 && props.canEdit && (
                                                <div className="tw-flex tw-flex-row tw-items-center">
                                                    <Button variant="primary" small onClick={() => arrayHelpers.push(getDefaultMilestone(values))}>
                                                        {$t('Add Date')}
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                />
                            </div>
                            {props.canEdit && (props.parentSection === 'expense' || (props.parentSection !== 'expense' && !isConfirmed)) && (
                                <div className="tw-flex tw-w-full tw-justify-end tw-gap-x-4 tw-my-4">
                                    {showCancelButton && (
                                        <Button variant="secondary" disabled={isSubmitting} onClick={redirectBack}>
                                            {$t('Cancel')}
                                        </Button>
                                    )}

                                    <Button variant="primary" disabled={isSubmitting} onClick={handleSubmit}>
                                        {props.submitButtonText}
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </Formik>
            </div>
        )
    );
}
const FormObserver = (props) => {
    const { values, setFieldValue } = useFormikContext();
    const valuesRef = useRef(values);
    valuesRef.current = values;

    // Only re-run when perc values or milestone count change — not when derived
    // fields (maxPercentage, uniqueKey) are updated by this effect, which would
    // otherwise cause an infinite loop via the enableReinitialize Formik cycle.
    const percSignature = values.dates?.map((m) => m.perc).join(',') ?? '';
    const datesLength = values.dates?.length ?? 0;

    useEffect(() => {
        const currentValues = valuesRef.current;
        let totalPercentage = 0;
        currentValues.dates?.forEach((milestone, index) => {
            totalPercentage += milestone.perc;
            if (totalPercentage > 100) {
                totalPercentage = 100;
            }
            const newMaxPercentage = props.getMaxPercentage(currentValues, index);
            const newUniqueKey = props.getUniqueKeyBasedOnIndex(currentValues, index);
            if (milestone.maxPercentage !== newMaxPercentage) {
                setFieldValue(`dates.${index}.maxPercentage`, newMaxPercentage);
            }
            if (milestone.uniqueKey !== newUniqueKey) {
                setFieldValue(`dates.${index}.uniqueKey`, newUniqueKey);
            }
        });
        props.valuesListener(totalPercentage);
    }, [percSignature, datesLength]);

    return null;
};
